import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import SessionStorage from 'utils/bom-dom-manipulation/session-storage';
import { ZINDEX_HIGH_1 } from 'utils/constants/app/ui';
import {
  MILLISECONDS_IN_SECOND,
  SECONDS_PER_MINUTE
} from 'utils/constants/dateInfo/time-numbers';
import { storageKeyName } from 'utils/generic/storage-key-name';
import { getCasinoSessionData } from 'services/casino/casino-session-data';
import { Modal, ModalButtons } from 'components/Modal';
import { updateQuery } from 'components/QueryRoute';
import { Button } from 'newcomponents/UI/Button';
import { useCasinoSingleGameClose } from 'hooks/casino/useCasinoSingleGameClose';
import { useCurrency } from 'hooks/useCurrency';
import { useSearchParams } from 'hooks/useSearchParams';
import { getIsLoggedIn } from 'store/selectors/user-data';
import './index.less';

type SessionData = {
  details: SessionDataDetails;
  result: number;
  result_text: null;
};

type SessionDataDetails = {
  CasinoBetTotal: number;
  CasinoWinTotal: number;
  Profitness: number;
  SessionDuration: number;
};

export const CasinoLimitModal = (): ReactElement => {
  const { t } = useTranslation();
  const { formatAmount } = useCurrency();
  const history = useHistory();
  const { game, mode } = useSearchParams();
  const isLoggedIn = useSelector(getIsLoggedIn);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [sessionData, setSessionData] = useState<SessionDataDetails>({
    CasinoBetTotal: 0,
    CasinoWinTotal: 0,
    Profitness: 0,
    SessionDuration: 0
  });

  const { handleCloseClick } = useCasinoSingleGameClose();

  const limitTime = useMemo(() => {
    return (
      JSON.parse(
        SessionStorage.getItem(
          storageKeyName('account', 'ACTIVE_TIME_IN_CASINO')
        )
      ) ?? 0
    );
  }, [isLoggedIn]);

  useEffect(() => {
    if (mode === 'real') {
      if (limitTime && !showLimitModal) {
        const timeoutId = setTimeout(() => {
          getCasinoSessionData((data: SessionData) => {
            if (data) {
              setSessionData(data.details);
              setShowLimitModal(true);
            }
          });
        }, limitTime * MILLISECONDS_IN_SECOND);

        return () => {
          clearTimeout(timeoutId);
        };
      }
    }
  }, [mode, game, showLimitModal, limitTime]);

  return (
    <Modal
      visible={showLimitModal}
      closable={false}
      maskClosable={false}
      centered={true}
      maxWidth={390}
      title={t('casino.casinoActivity')}
      className="x-casinoLimitModal"
      image={false}
      zIndex={ZINDEX_HIGH_1}
    >
      <p className="x-casinoLimitModal__description">
        {t(`casino.continueOrNot`)}
      </p>
      <ul className="x-casinoLimitModal__activityList">
        <li className="x-casinoLimitModal__activityList__item">
          {t('casino.playedFor')}
          <span>
            {limitTime / SECONDS_PER_MINUTE} {t(`account.minute`)}
          </span>
        </li>
        <li className="x-casinoLimitModal__activityList__item">
          {t('casino.betCount')}
          <span>{formatAmount(sessionData.CasinoBetTotal)}</span>
        </li>
        <li className="x-casinoLimitModal__activityList__item">
          {t('casino.winCount')}
          <span>{formatAmount(sessionData.CasinoWinTotal)}</span>
        </li>
        <li className="x-casinoLimitModal__activityList__item">
          {t('casino.sessionResult')}
          <span>{formatAmount(sessionData.Profitness)}</span>
        </li>
      </ul>
      <ModalButtons direction="column">
        <Button
          size="large"
          type="primary"
          onClick={() => setShowLimitModal(false)}
        >
          {t('casino.continue')}
        </Button>
        <Button
          size="large"
          type="text"
          onClick={() => {
            handleCloseClick();
            history.push(
              updateQuery({ accounts: '*', 'balance-history': '*' })
            );
          }}
        >
          {t(`casino.openBalanceHistory`)}
        </Button>
      </ModalButtons>
    </Modal>
  );
};
